import { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { Utensils } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * One-click lunch break toggle.
 * - First click → starts the lunch break (auto-pauses any running task timer).
 * - Second click (while running) → ends it. Shows live HH:MM:SS while active.
 * Available to all roles. Logged in the daily timesheet.
 */
export const LunchBreakButton = () => {
  const [active, setActive] = useState(null); // {id, started_at} or null
  const [displaySec, setDisplaySec] = useState(0);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef(null);

  // On mount, hydrate state
  useEffect(() => {
    let alive = true;
    api.get('/lunch-break/active')
      .then(res => { if (alive) setActive(res.data || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Live timer
  useEffect(() => {
    if (!active) {
      setDisplaySec(0);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    const compute = () => {
      try {
        const started = new Date(active.started_at).getTime();
        const sec = Math.max(0, Math.floor((Date.now() - started) / 1000));
        setDisplaySec(sec);
      } catch { /* noop */ }
    };
    compute();
    tickRef.current = setInterval(compute, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [active]);

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return h > 0
      ? `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`
      : `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
  };

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post('/lunch-break/toggle');
      if (res.data.state === 'started') {
        setActive({ id: res.data.id, started_at: res.data.started_at });
        toast.success('Lunch break started — enjoy! 🍽️');
        if (res.data.paused_task_id) {
          window.dispatchEvent(new CustomEvent('ws-notification', { detail: { type: 'timer_paused' } }));
        }
      } else {
        const mins = res.data.duration_minutes || 0;
        setActive(null);
        toast.success(`Lunch break ended (${mins} min logged in timesheet)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle lunch break');
    } finally {
      setBusy(false);
    }
  };

  if (active) {
    return (
      <Button
        size="default"
        onClick={toggle}
        disabled={busy}
        className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg ring-2 ring-orange-300 gap-1.5 rounded-full px-4"
        data-testid="lunch-break-btn-active"
        title="End lunch break"
      >
        <Utensils className="h-4 w-4" />
        <span>On lunch</span>
        <span className="font-mono text-xs bg-white/20 px-1.5 py-0.5 rounded">{fmt(displaySec)}</span>
      </Button>
    );
  }

  return (
    <Button
      size="default"
      onClick={toggle}
      disabled={busy}
      className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg hover:shadow-xl ring-2 ring-orange-200 gap-1.5 rounded-full px-4"
      data-testid="lunch-break-btn"
      title="Start lunch break"
    >
      <Utensils className="h-4 w-4" />
      <span>Lunch</span>
    </Button>
  );
};
